/**
 * Live capacity/status controller for the venue booking wizard.
 */
(function (global) {
  'use strict';

  var DEFAULT_POLL_INTERVAL_MS = 5000;
  var MOBILE_POLL_INTERVAL_MS = 15000;

  function cityEvents() {
    return global.WorkshopCityEvents || null;
  }

  function slotIsBookable(slot) {
    if (!slot) return false;
    var api = cityEvents();
    if (api && typeof api.isSlotBookable === 'function') return api.isSlotBookable(slot);
    var cap = slot.capacity_remaining;
    if (cap == null || cap === '') cap = slot._raw && slot._raw.capacity_remaining;
    if (cap != null && cap !== '' && Number(cap) <= 0) return false;
    var st = String(slot.status || '').toLowerCase();
    if (st === 'sold_out' || st === 'cancelled' || st === 'draft' || st === 'completed') return false;
    var ds = String(slot.display_status || '').toLowerCase();
    if (ds === 'sold_out' || ds === 'hidden' || ds === 'ended') return false;
    return true;
  }

  function orderQtyCap(root) {
    var CFG = bookingConfig();
    var n = 20;
    if (CFG && typeof CFG.readBootstrap === 'function') {
      var enforce = (CFG.readBootstrap(root) || {}).enforce || {};
      if (enforce.max_qty_per_order != null && enforce.max_qty_per_order !== '') {
        n = Number(enforce.max_qty_per_order);
      }
    }
    if (!Number.isFinite(n) || n <= 0) return Infinity;
    return n;
  }

  /* Seat pool for a session, then the per-order cap from booking config.
     Infinity remaining = live capacity not on the slot yet — still bound by
     max_qty_per_order (20), never a fake 1-seat lock. */
  function maxBookableQty(slot, root) {
    var api = cityEvents();
    var seats = Infinity;
    if (api && typeof api.remainingSeats === 'function') {
      var remaining = api.remainingSeats(slot);
      seats = remaining == null ? Infinity : remaining;
    } else if (!slotIsBookable(slot)) {
      seats = 0;
    } else {
      var cap = slot.capacity_remaining;
      if (cap == null || cap === '') cap = slot._raw && slot._raw.capacity_remaining;
      if (cap == null || cap === '') seats = Infinity;
      else {
        var n = Number(cap);
        seats = Number.isFinite(n) && n > 0 ? n : 0;
      }
    }
    return Math.min(seats, orderQtyCap(root));
  }

  /* Fetch live state, then merge. Passing the options object as `states` used
     to no-op the merge, so capacity_remaining never arrived and the picker
     collapsed to a fake 1-seat cap. */
  function enrichSlots(slots, options) {
    var api = cityEvents();
    var list = Array.isArray(slots) ? slots : [];
    options = options || {};
    if (!api || typeof api.fetchLiveState !== 'function' || typeof api.mergeSlotsWithLive !== 'function') {
      return Promise.resolve(list);
    }
    if (options.fetch === false) return Promise.resolve(list);
    var gids = typeof api.collectEventGids === 'function' ? api.collectEventGids(list) : [];
    if (!gids.length) return Promise.resolve(list);
    return api
      .fetchLiveState(gids, { root: options.root, timeoutMs: options.timeoutMs })
      .then(function (r) {
        options._liveOk = !(r && r.ok === false);
        return api.mergeSlotsWithLive(list, (r && r.states) || {}, r && r.as_of);
      })
      .catch(function () {
        options._liveOk = false;
        return list;
      });
  }

  function ensureFreshSlot(slot, options) {
    if (!slot) return Promise.resolve(null);
    return enrichSlots([slot], options).then(function (slots) {
      var fresh = slots && slots[0] ? slots[0] : slot;
      if (!slotIsBookable(fresh)) return null;
      return fresh;
    });
  }

  function isMobilePollViewport() {
    try {
      return !!(global.matchMedia && global.matchMedia('(max-width: 749px)').matches);
    } catch (_) {
      return false;
    }
  }

  function readPollInterval(root, config) {
    config = config || {};
    var api = cityEvents();
    if (api && typeof api.resolvePollIntervalMs === 'function') {
      // Pass 0 (not null) as the fallback: resolvePollIntervalMs returns its
      // internal 5000 default when the fallback is null, which would masquerade
      // as real config and clobber the mobile default below. With 0 it returns
      // 0 when unconfigured, so we correctly fall through to the viewport default.
      var fromConfig = api.resolvePollIntervalMs('booking', 0);
      if (fromConfig > 0) return fromConfig;
    }
    if (config.pollIntervalMs != null) {
      var fromOpt = Number(config.pollIntervalMs) || 0;
      if (fromOpt > 0) return fromOpt;
    }
    var attr = root && root.getAttribute && root.getAttribute('data-wbw-live-poll-interval');
    if (attr != null && attr !== '') {
      var fromAttr = Number(attr) || 0;
      if (fromAttr > 0) return fromAttr;
    }
    if (global.__WBW_LIVE_POLL_INTERVAL_MS__ != null) {
      var fromGlobal = Number(global.__WBW_LIVE_POLL_INTERVAL_MS__) || 0;
      if (fromGlobal > 0) return fromGlobal;
    }
    if (isMobilePollViewport()) return MOBILE_POLL_INTERVAL_MS;
    return DEFAULT_POLL_INTERVAL_MS;
  }

  function liveOptions(root, config) {
    config = config || {};
    return {
      root: root,
      productHandle:
        config.productHandle ||
        (root && root.getAttribute && root.getAttribute('data-product-handle')) ||
        '',
      shopDomain:
        config.shopDomain ||
        (global.Shopify && global.Shopify.shop ? String(global.Shopify.shop) : ''),
    };
  }

  function bookingConfig() {
    return global.WorkshopBookingConfig || global.WorkshopCityConfig || null;
  }

  /* No verdict available. Blocking here would strand every visitor whenever the
     capacity gate is switched off, so only fail closed when the gate is armed
     and its policy actually asks for it. */
  function unverifiedVerdict(root, intent) {
    var CFG = bookingConfig();
    if (!CFG || CFG.TEMP_DISABLE_CAPACITY_VALIDATE === true) return { allowed: true };
    if (typeof CFG.validateGateEnabled === 'function' && !CFG.validateGateEnabled(root, intent)) {
      return { allowed: true };
    }
    if (typeof CFG.validateFailModeOpen === 'function' && CFG.validateFailModeOpen(root)) {
      return { allowed: true };
    }
    return {
      allowed: false,
      message: 'We could not verify workshop availability. Please try again.',
    };
  }

  function slotEventId(slot) {
    var raw = (slot && slot._raw) || {};
    return String(
      (slot && (slot.canonical_event_id || slot.event_metaobject_gid || slot.gid)) ||
        raw.canonical_event_id ||
        raw.event_metaobject_gid ||
        raw.gid ||
        ''
    ).trim();
  }

  function subtractCartSeatsEnabled(root) {
    var CFG = bookingConfig();
    var enforce =
      CFG && typeof CFG.readBootstrap === 'function' ? (CFG.readBootstrap(root) || {}).enforce || {} : {};
    return enforce.subtract_cart_seats !== false;
  }

  function cartSeatsForSlot(slot, options) {
    options = options || {};
    if (!subtractCartSeatsEnabled(options.root)) return Promise.resolve(0);
    if (options.cartSeats != null && options.cartSeats !== '') {
      var n = Number(options.cartSeats);
      return Promise.resolve(Number.isFinite(n) && n > 0 ? n : 0);
    }
    var api = cityEvents();
    if (options.cart && api && typeof api.seatsInCartForSlot === 'function') {
      return Promise.resolve(api.seatsInCartForSlot(slot, options.cart) || 0);
    }
    if (api && typeof api.fetchCartSeatsForSlot === 'function') {
      return api.fetchCartSeatsForSlot(slot);
    }
    return Promise.resolve(0);
  }

  function validateSlotForCart(slot, requestedAddQty, options) {
    var opts = Object.assign({ intent: 'add_to_cart' }, options || {});
    var api = cityEvents();
    if (api && typeof api.validateAddToCart === 'function') {
      return api.validateAddToCart(slot, requestedAddQty, opts);
    }
    var eventId = slotEventId(slot);
    if (!api || typeof api.fetchCapacityValidate !== 'function' || !eventId) {
      return Promise.resolve(unverifiedVerdict(opts.root, opts.intent));
    }
    return cartSeatsForSlot(slot, opts).then(function (inCart) {
      var addQty = Number(requestedAddQty) || 0;
      return api.fetchCapacityValidate(opts.root || null, {
        event_id: eventId,
        requested_total_qty: addQty + (Number(inCart) || 0),
        intent: opts.intent,
      });
    })
      .then(function (verdict) {
        return verdict || unverifiedVerdict(opts.root, opts.intent);
      })
      .catch(function () {
        return unverifiedVerdict(opts.root, opts.intent);
      });
  }

  function createController(root, config) {
    config = config || {};
    var timer = null;
    var stopped = false;
    var opts = liveOptions(root, config);
    var interval = readPollInterval(root, config);

    var pollGetSlots = null;
    var pollOnUpdate = null;
    var tickInFlight = false;
    var pendingForceTick = false;
    var visibilityBound = false;
    var streamPaused = false;

    var api = cityEvents();
    if (api && typeof api.ensureBookingConfig === 'function') {
      api.ensureBookingConfig(opts).then(function () {
        if (typeof api.resolvePollIntervalMs === 'function') {
          var next = api.resolvePollIntervalMs('booking', interval);
          if (next > 0 && next !== interval) {
            interval = next;
            // Do not resurrect the interval while paused for SSE; startPoll is
            // a no-op timer-wise when streamPaused, and resumeFromStream will
            // apply the updated interval on reconnect fallback.
            if (pollGetSlots && !streamPaused) startPoll(pollGetSlots, pollOnUpdate, { immediate: false });
          }
        }
      });
    }

    function tickNow(getSlots, onUpdate, tickOpts) {
      tickOpts = tickOpts || {};
      var force = tickOpts.force === true;
      if (stopped || typeof getSlots !== 'function') return Promise.resolve({ ok: false });
      if (!force && global.document && global.document.hidden) return Promise.resolve({ ok: false });
      var slots = getSlots() || [];
      if (!slots.length) return Promise.resolve({ ok: true });
      if (tickInFlight) {
        if (force) pendingForceTick = true;
        return Promise.resolve({ ok: false, deferred: true });
      }
      tickInFlight = true;
      var enrichOpts = Object.assign({}, opts, force ? { force: true } : {});
      return enrichSlots(slots, enrichOpts)
        .then(function (freshSlots) {
          if (!stopped && typeof onUpdate === 'function') {
            onUpdate(freshSlots || slots, { at: Date.now() });
          }
          return { ok: enrichOpts._liveOk !== false, slots: freshSlots };
        })
        .catch(function () {
          return { ok: false };
        })
        .finally(function () {
          tickInFlight = false;
          if (pendingForceTick && !stopped && pollGetSlots && pollOnUpdate) {
            pendingForceTick = false;
            tickNow(pollGetSlots, pollOnUpdate, { force: true });
          }
        });
    }

    function startPoll(getSlots, onUpdate, pollOptions) {
      pollOptions = pollOptions || {};
      var immediate = pollOptions.immediate !== false;
      stopPoll();
      pollGetSlots = getSlots;
      pollOnUpdate = onUpdate;
      // While the SSE stream drives updates, keep slot refs but run no timer/tick.
      if (streamPaused) return null;
      if (immediate) tickNow(getSlots, onUpdate);
      if (!(interval > 0)) return null;
      timer = global.setInterval(function () {
        tickNow(getSlots, onUpdate);
      }, interval);
      if (global.document && !visibilityBound) {
        visibilityBound = true;
        global.document.addEventListener('visibilitychange', function () {
          if (global.document.hidden) return;
          if (stopped || !pollGetSlots || !pollOnUpdate) return;
          // SSE drives updates while paused; the wizard rebinds/refreshes the
          // stream on focus, so a hot force-tick here would just double-fetch.
          if (streamPaused) return;
          tickNow(pollGetSlots, pollOnUpdate, { force: true });
        });
      }
      return timer;
    }

    function stopPoll() {
      if (timer) {
        global.clearInterval(timer);
        timer = null;
      }
    }

    return {
      enrichSlots: function (slots, extra) {
        return enrichSlots(slots, Object.assign({}, opts, extra || {}));
      },
      ensureFreshSlot: function (slot) {
        return ensureFreshSlot(slot, opts);
      },
      validateSlotForCart: function (slot, requestedAddQty, extra) {
        return validateSlotForCart(slot, requestedAddQty, Object.assign({}, opts, extra || {}));
      },
      maxBookableQty: function (slot) {
        return maxBookableQty(slot, root);
      },
      startPoll: startPoll,
      pauseForStream: function () {
        streamPaused = true;
        stopPoll();
      },
      resumeFromStream: function () {
        if (!streamPaused) return;
        streamPaused = false;
        if (pollGetSlots && pollOnUpdate) startPoll(pollGetSlots, pollOnUpdate, { immediate: false });
      },
      restartPoll: function (pollOptions) {
        if (pollGetSlots && pollOnUpdate) startPoll(pollGetSlots, pollOnUpdate, pollOptions);
      },
      tickNow: function (tickOpts) {
        if (pollGetSlots && pollOnUpdate) return tickNow(pollGetSlots, pollOnUpdate, tickOpts);
        return Promise.resolve({ ok: false });
      },
      stop: function () {
        stopped = true;
        stopPoll();
      },
    };
  }

  global.WorkshopBookingWizardLive = {
    createController: createController,
    enrichSlots: enrichSlots,
    ensureFreshSlot: ensureFreshSlot,
    validateSlotForCart: validateSlotForCart,
    maxBookableQty: maxBookableQty,
    startPoll: function (slots, onUpdate, options) {
      var root = options && options.root;
      return createController(root, options).startPoll(
        function () {
          return typeof slots === 'function' ? slots() : slots;
        },
        onUpdate
      );
    },
  };
})(typeof window !== 'undefined' ? window : this);
